import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  linkPlugin,
  linkDialogPlugin,
  diffSourcePlugin,
  toolbarPlugin,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  ListsToggle,
  CreateLink,
  Separator,
  DiffSourceToggleWrapper,
} from "@mdxeditor/editor";
import type {
  MarkdownEditorProps,
  MarkdownEditorRef,
} from "features/markdown/blocks/MarkdownEditor/MarkdownEditor.types";
import { sanitizeMarkdown } from "features/markdown/utils";
import i18next from "i18next";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { mdxEditorTranslationRu } from "./MdxEditor.i18n.ru";

import "@mdxeditor/editor/style.css";
import "./MdxEditor.css";

i18next.init({
  lng: "ru",
  debug: false,
  resources: { ru: { translation: mdxEditorTranslationRu } },
});

const MdxEditor = forwardRef<MarkdownEditorRef, MarkdownEditorProps>(
  (
    {
      initialValue = "",
      placeholder = "Введите текст...",
      showModeSwitch = false,
      onChange,
      height = "100%",
    },
    ref,
  ) => {
    const [markdown, setMarkdown] = useState(sanitizeMarkdown(initialValue));
    const editorRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
      getMarkdown: () => sanitizeMarkdown(markdown),
      setMarkdown: (value: string) => {
        const clean = sanitizeMarkdown(value);
        setMarkdown(clean);
        // MDXEditor's `markdown` prop is read only on mount — updating our own
        // state alone doesn't refresh what's on screen, so also call through
        // to the library's own imperative setMarkdown.
        editorRef.current?.setMarkdown(clean);
      },
      getInstance: () => editorRef.current,
    }));

    const handleChange = useCallback(
      (value: string) => {
        const clean = sanitizeMarkdown(value);
        setMarkdown(clean);
        onChange?.(clean);
      },
      [onChange],
    );

    const plugins = useMemo(
      () => [
        diffSourcePlugin({ viewMode: "rich-text", diffMarkdown: "" }),
        headingsPlugin({ allowedHeadingLevels: [3] }),
        listsPlugin(),
        linkPlugin(),
        linkDialogPlugin({ showLinkTitleField: false }),
        markdownShortcutPlugin(),
        toolbarPlugin({
          toolbarContents: () => (
            <>
              <BlockTypeSelect />
              <Separator />
              <BoldItalicUnderlineToggles options={["Bold", "Italic"]} />
              <Separator />
              <ListsToggle options={["bullet", "number"]} />
              <Separator />
              <CreateLink />
              {showModeSwitch && (
                <>
                  <Separator />
                  <DiffSourceToggleWrapper options={["rich-text", "source"]}>
                    <></>
                  </DiffSourceToggleWrapper>
                </>
              )}
            </>
          ),
        }),
      ],
      [showModeSwitch],
    );

    return (
      <div style={{ height, overflowY: "auto" }}>
        <MDXEditor
          ref={editorRef}
          markdown={markdown}
          onChange={handleChange}
          translation={i18next.t.bind(i18next)}
          contentEditableClassName="markdown-editor"
          placeholder={placeholder}
          plugins={plugins}
        />
      </div>
    );
  },
);

MdxEditor.displayName = "MdxEditor";

export default MdxEditor;
